import { toMoney, moneyToNumber } from '../amortization/decimal.js';

export type MoraInput = {
  /** Money: outstanding lot (principal+interest) on the installment. */
  unpaidCuota: string;
  /** 'YYYY-MM-DD' */
  dueDate: string;
  /** 'YYYY-MM-DD' — the payment date, or "today" for read-time display. */
  asOf: string;
  /** Monthly mora rate as a fraction, e.g. '0.05'. */
  monthlyRate: string;
  graceDays: number;
};

const MS_PER_DAY = 86_400_000;

/** Whole calendar days from `dueDate` to `asOf` (negative if asOf precedes dueDate). */
export function daysBetween(dueDate: string, asOf: string): number {
  const a = Date.parse(`${dueDate}T00:00:00Z`);
  const b = Date.parse(`${asOf}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) throw new Error(`invalid date: ${dueDate} / ${asOf}`);
  return Math.round((b - a) / MS_PER_DAY);
}

/**
 * Mora on a single overdue installment: a monthly rate on the unpaid cuota,
 * charged per whole 30-day period late beyond the grace window. Simple (no
 * compounding). Pure — no DB/IO, no clock. Mora applies only to the lot cuota,
 * never to the admin fee.
 */
export function moraForInstallment(i: MoraInput): { monthsLate: number; moraAmount: string } {
  const overGrace = daysBetween(i.dueDate, i.asOf) - i.graceDays;
  if (overGrace <= 0) return { monthsLate: 0, moraAmount: '0.00' };
  const monthsLate = Math.ceil(overGrace / 30);
  const moraAmount = toMoney(moneyToNumber(i.unpaidCuota) * Number(i.monthlyRate) * monthsLate, 'half_even');
  return { monthsLate, moraAmount };
}
